"use client";

import React, {Suspense, useEffect, useState, useCallback, useRef} from "react";
import dynamic from "next/dynamic";

// Dynamically imported components
const Tabs = dynamic(() => import("@/components/ui/tabs").then((mod) => mod.Tabs), {ssr: false});
const TabsList = dynamic(() => import("@/components/ui/tabs").then((mod) => mod.TabsList), {ssr: false});
const TabsTrigger = dynamic(() => import("@/components/ui/tabs").then((mod) => mod.TabsTrigger), {ssr: false});
const TabsContent = dynamic(() => import("@/components/ui/tabs").then((mod) => mod.TabsContent), {ssr: false});
const CollapsibleContentComponent = dynamic(() => import("@/components/ui/collapsible-content/collapsible-content"), {ssr: false});

import { SingleProductEnum } from "@/constants/commerce-enum";
import { IProductDetailsProps } from "@/interface/components/product/product-details";

interface Section {
    id: string;
    title: string;
    tabValue?: string;
}

const sections: Section[] = [
    {id: "product-information-md", title: "اطلاعات کالا"},
    {id: "comment-header-section", title: "دیدگاه کاربران"},
];

function ScrollNavigationComment({activeTab, setActiveTab}: {
    activeTab?: string;
    setActiveTab?: (value: string) => void
}) {
    const [isVisible, setIsVisible] = useState(false);
    const [activeSection, setActiveSection] = useState("");
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isProgrammaticScroll = useRef(false);

    const handleScroll = useCallback(() => {
        if (isProgrammaticScroll.current) {
            isProgrammaticScroll.current = false;
            return;
        }

        const scrollPosition = window.scrollY + 100; // Offset for fixed header

        let currentSection = "";
        for (const section of sections) {
            const element = document.getElementById(section.id);
            if (element) {
                const elementTop = element.offsetTop;
                const elementBottom = elementTop + element.offsetHeight;

                if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
                    currentSection = section.id;
                    if (setActiveTab && section.tabValue && activeTab !== section.tabValue) {
                        setActiveTab(section.tabValue);
                    }
                    break;
                }
            }
        }

        setActiveSection(currentSection);

        const commentHeaderSection = document.getElementById("comment-header-section");
        if (commentHeaderSection) {
            setIsVisible(scrollPosition >= commentHeaderSection.offsetTop - 100);
        }
    }, [setActiveTab, activeTab]);

    const scrollToSection = useCallback((sectionId: string, tabValue?: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            isProgrammaticScroll.current = true;
            setActiveSection(sectionId);

            if (tabValue && setActiveTab && activeTab !== tabValue) {
                setActiveTab(tabValue);
            }

            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });

            scrollTimeoutRef.current = setTimeout(() => {
                isProgrammaticScroll.current = false;
            }, 1000);
        }
    }, [setActiveTab, activeTab]);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        handleScroll(); // Check initial state

        return () => {
            window.removeEventListener("scroll", handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [handleScroll]);

    if (!isVisible) {
        return null;
    }

    return (
        <div
            className="w-full flex lg:hidden h-[55px] px-[23px] fixed top-12 left-0 text-sm bg-white shadow-sm border-t border-gray-100 z-50 items-center justify-center gap-10">
            {sections.map((section) => (
                <button
                    key={section.id}
                    className={`pb-2 cursor-pointer ${
                        activeSection === section.id
                            ? "border-orange-primary border-b-2 text-orange-primary"
                            : "text-gray-600 hover:text-orange-primary"
                    }`}
                    onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(section.id, section.tabValue);
                    }}
                >
                    {section.title}
                </button>
            ))}
        </div>
    );
}

function DescriptionDetailsTabComponent({data}: { data: IProductDetailsProps }) {
    const [activeTab, setActiveTab] = useState("description");
    const isProgrammaticTabChange = useRef(false);

    const handleTabChange = useCallback((value: string) => {
        if (isProgrammaticTabChange.current) {
            isProgrammaticTabChange.current = false;
            return;
        }
        setActiveTab(value);
    }, []);

    useEffect(() => {
        // When tab changes, scroll to the corresponding section
        if (!isProgrammaticTabChange.current) {
            const section = sections.find(s => s.tabValue === activeTab);
            if (section) {
                const element = document.getElementById(section.id);
                if (element) {
                    const offset = 100;
                    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - offset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth"
                    });
                }
            }
        }
    }, [activeTab]);

    if (!data) return null;

    return (
        <>
            <ScrollNavigationComment activeTab={activeTab} setActiveTab={setActiveTab}/>

            <Suspense fallback={<div>{SingleProductEnum.loading}</div>}>
                <Tabs
                    dir={"rtl"}
                    value={activeTab}
                    onValueChange={handleTabChange}
                    className="w-full p-2"
                >
                    <TabsList className="w-full flex items-center justify-center" id={"product-information-md"}>
                        <TabsTrigger value="description">{SingleProductEnum.productDescription}</TabsTrigger>
                        <TabsTrigger value="details">{SingleProductEnum.productDetails}</TabsTrigger>
                    </TabsList>

                    <TabsContent id="product-description-md" value="description">
                        <CollapsibleContentComponent content={data?.product?.content} maxHeight={500}/>
                    </TabsContent>

                    <TabsContent id="product-specifications-md" value="details" className="p-0 mb-0">
                        <section className="w-full my-5 items-center gap-x-20 2xl:gap-x-36 flex flex-wrap">
                            {data?.attributeOptions && Object.keys(data?.attributeOptions)?.map((attr, index) => (
                                <div key={index} className="w-full md:w-1/4 flex flex-wrap">
                                    <div
                                        className="w-full text-sm flex-wrap py-4 border-b flex overflow-hidden items-center justify-between">
                                        <span className="text-gray-400 font-medium">{attr}</span>
                                        <span
                                            className="font-medium">{data?.attributeOptions[attr].join(",", "\n")}</span>
                                    </div>
                                </div>
                            ))}

                            {data?.product?.product_import_code && (
                                <div className="w-full md:w-1/4 flex flex-wrap">
                                    <div
                                        className="w-full text-sm flex-wrap py-4 border-b flex overflow-hidden items-center justify-between">
                                        <span className="text-gray-400 font-medium">{SingleProductEnum.productId}</span>
                                        <span className="font-medium">{data?.product?.product_import_code}</span>
                                    </div>
                                </div>
                            )}

                            {data?.product?.health_ministry_code && (
                                <div className="w-full md:w-1/4 flex flex-wrap">
                                    <div
                                        className="w-full text-sm flex-wrap py-4 border-b flex overflow-hidden items-center justify-between">
                                        <span
                                            className="text-gray-400 font-medium">{SingleProductEnum.ministryOfHealthCode}</span>
                                        <span className="font-medium">{data?.product?.health_ministry_code}</span>
                                    </div>
                                </div>
                            )}
                        </section>
                    </TabsContent>
                </Tabs>
            </Suspense>
        </>
    );
}

export default DescriptionDetailsTabComponent;